import { type ExternalToast, toast } from 'sonner';
import { errorMessage, isApiError } from '~/api/client';

type Options = Pick<ExternalToast, 'id' | 'duration' | 'action'>;

/**
 * The one way a screen speaks up in a toast. Titles follow the copy rules:
 * a success says what happened ("Archived."), a failure says what did not
 * ("Could not archive.") and the API's sentence goes underneath.
 *
 * Offline, rate limits, server trouble and an ended session are already
 * toasted by the global handler (src/lib/queryClient.ts); `notify.error`
 * stays quiet for those so the person hears it once.
 */
export const notify = {
  success(title: string, description?: string, options?: Options) {
    toast.success(title, { description, ...options });
  },
  /** Plain news that is neither good nor bad, like an incoming notification. */
  message(title: string, description?: string, options?: Options) {
    toast(title, { description, ...options });
  },
  info(title: string, description?: string, options?: Options) {
    toast.info(title, { description, ...options });
  },
  warning(title: string, description?: string, options?: Options) {
    toast.warning(title, { description, ...options });
  },
  /**
   * A failure. Pass the caught error (its sentence becomes the description)
   * or a sentence of your own. Silent for an error the global handler took.
   */
  error(title: string, error?: unknown, options?: Options) {
    if (isApiError(error) && error.handled) return;
    // The person closed a question on purpose (ClockInCancelled): nothing failed.
    if (error instanceof Error && (error as Error & { silent?: boolean }).silent) return;
    const description =
      error === undefined ? undefined : typeof error === 'string' ? error : errorMessage(error);
    toast.error(title, { description, ...options });
  },
  dismiss(id?: string | number) {
    toast.dismiss(id);
  },
};

/**
 * Toasts for a one-line `mutate`, so an action needs no try/catch:
 *
 *   archive.mutate(id, feedback({ success: 'Archived.', error: 'Could not archive.' }))
 *
 * `success` may be built from the response and the variables.
 */
export function feedback<TData = unknown, TVariables = unknown>(messages: {
  success?: string | ((data: TData, variables: TVariables) => string | undefined);
  error: string;
}) {
  return {
    onSuccess: (data: TData, variables: TVariables) => {
      const title =
        typeof messages.success === 'function'
          ? messages.success(data, variables)
          : messages.success;
      if (title) notify.success(title);
    },
    onError: (error: unknown) => notify.error(messages.error, error),
  };
}
